import {v4 as uuidv4} from 'uuid'

/**
 * The viewer↔host contract for choosing a file.
 *
 * A wizard inside a viewer iframe cannot browse storage: it has no credentials, no listing endpoint
 * and no idea what a folder is here. So the widget asks, and the host — which already owns the file
 * manager and the Cognito session — answers with a storage key.
 *
 * Why the host and not the iframe:
 *  - one dialog for the whole page, centred on the page rather than inside whichever iframe asked.
 *    An in-iframe dialog would be centred in a panel that may be a few hundred pixels tall;
 *  - a file dialog is a one-of-a-kind thing. Two viewers must not both be driving one, so requests
 *    are served one at a time and the rest queue (see `FilePickerHost`);
 *  - nothing about auth or storage leaks into `web-libraries`, which is shared with the desktop
 *    embed where none of it exists.
 */

/** Viewer → host. Added to `HandlerAction`, so the managers' `action in this` dispatch ignores it. */
export const REQUEST_FILE_PICK = 'requestFilePick'

/** Host → viewer: a file was chosen. */
export const FILE_PICKED = 'filePicked'

/** Host → viewer: the user closed the dialog without choosing. */
export const FILE_PICK_CANCELLED = 'filePickCancelled'

/** What a widget sends when it wants a file. */
export type FilePickRequest = {
  action: typeof REQUEST_FILE_PICK
  /** Correlates the reply. The widget ignores replies that are not its own. */
  reqId: string
  value?: {
    /**
     * Extension hints from the parameter's own file filters, e.g. `['*.obo']`. Advisory: the list is
     * filtered by them, and the user can still switch the filter off. The server validates.
     */
    filters?: string[]
    /** Dialog title, e.g. "Select GO-Slim obo file" — the parameter's own message. */
    title?: string
    /**
     * The widget's current value, an `s3://<bucket>/<poolId>/<key>` URI, so the dialog can open in
     * the folder that names and preselect the file rather than starting at the root every time.
     *
     * Optional, and it must stay that way: viewers ship on their own cadence, so a build older than
     * this field simply omits it and the dialog opens at the root exactly as it used to.
     */
    currentUri?: string
    /**
     * Let the user choose several files at once. The reply then carries {@link FilePickReply.values}.
     *
     * A flag on this message rather than a new action name, for the same cadence reason as
     * `currentUri` — and here it cuts both ways. An older *host* ignores the flag, serves
     * single-select and answers as it always has, which the multi-file widget appends exactly as it
     * appends one of several; a new action name would have gone unanswered and left that widget stuck
     * on its no-host fallback. An older *widget* never sends it and is served single-select.
     */
    multiple?: boolean
  }
}

/** What the host sends back. `value` is absent on a cancel. */
export type FilePickReply = {
  action: typeof FILE_PICKED | typeof FILE_PICK_CANCELLED
  reqId: string
  value?: {
    /**
     * The full storage URI, `s3://<bucket>/<poolId>/<key>` — and **the value the widget submits**.
     *
     * A URI rather than the bare key, because the server side needs a scheme. `FileUniform` reads
     * `s3://` and `file:` URIs and enforces the caller's own `identityId` prefix on them, and
     * `FuriUtils.fromURI` — the only way to get a value into a `FuriKey` — rejects anything without
     * one. Built with the same `getFileS3Url` the app already uses for `/metadata`, so a picked file
     * and an opened file are named identically.
     */
    uri: string
    /**
     * The storage key as the file manager's rows carry it (`dbItem.file`), relative to the user's
     * prefix. Not the submitted value; kept so a caller can show or re-list it without re-parsing.
     */
    key: string
    /** Basename, so the widget can label the field without parsing the URI. */
    name: string
  }
  /**
   * Every file chosen, when {@link FilePickRequest.value.multiple} was asked for.
   *
   * `value` is always set as well, to the first entry, so a single-file widget reading only `value`
   * is never handed nothing. Absent entirely for a single-selection pick, which is what makes a
   * widget that has never heard of this field behave exactly as before.
   */
  values?: Array<{uri: string; key: string; name: string}>
}

/**
 * Whether a message is a pick request.
 *
 * Checks only `action` and `reqId`, so it needs no change as `value` grows fields — a widget older
 * than `multiple` (or than `currentUri`) still validates, which is what keeps the contract additive.
 */
export function isFilePickRequest(data: unknown): data is FilePickRequest {
  return (
    !!data &&
    typeof data === 'object' &&
    (data as {action?: unknown}).action === REQUEST_FILE_PICK &&
    typeof (data as {reqId?: unknown}).reqId === 'string'
  )
}

export function createFilePickRequest(value?: FilePickRequest['value']): FilePickRequest {
  return {action: REQUEST_FILE_PICK, reqId: uuidv4(), value}
}

export function createFilePickedReply(
  reqId: string,
  uri: string,
  key: string,
  name: string
): FilePickReply {
  return {action: FILE_PICKED, reqId, value: {uri, key, name}}
}

/**
 * Reply for a multiple selection.
 *
 * Sets `value` to the first entry as well as `values`, so the reply is readable by a widget that
 * predates multi-selection. An empty list is not a pick — cancel instead.
 */
export function createFilePickedManyReply(
  reqId: string,
  files: Array<{uri: string; key: string; name: string}>
): FilePickReply {
  return {action: FILE_PICKED, reqId, value: files[0], values: files}
}

export function createFilePickCancelledReply(reqId: string): FilePickReply {
  return {action: FILE_PICK_CANCELLED, reqId}
}

/**
 * Whether a filename passes the request's filters.
 *
 * Filters arrive in the desktop's glob form (`*.obo`, and occasionally `*.obo;*.txt` in one entry),
 * because they come straight from `FileExtension` on the parameter key. Only the extension is
 * honoured — a full glob match would be more than any of these filters actually express. `*` and
 * `*.*` accept everything.
 */
export function matchesFilters(name: string, filters?: string[]): boolean {
  if (!filters || filters.length === 0) {
    return true
  }

  const extensions = filters
    .flatMap((filter) => filter.split(/[;,]/))
    .map((filter) => filter.trim().toLowerCase())
    .filter((filter) => filter.length > 0)

  if (extensions.length === 0 || extensions.some((filter) => filter === '*' || filter === '*.*')) {
    return true
  }

  const lower = name.toLowerCase()

  return extensions.some((filter) => {
    const bare = filter.startsWith('*.') ? filter.slice(1) : filter.startsWith('*') ? filter.slice(1) : filter
    return bare.startsWith('.') ? lower.endsWith(bare) : lower === bare
  })
}
